import {useState, useEffect} from "react"
import { useSelector, useDispatch } from "react-redux"
import {Navigate} from "react-router-dom"
import Modal from "../../components/Modal"
import PayeeOrderCheckout from "../PayeeOrderCheckout"

import useIsOpen from "../../hooks/useIsOpen"

import { PAYEE_CATALOG_TYPE_ORDERING } from "../../constants/payeeCatalog"

import { getCartTotal } from "../../utils/cartUtils"
import { getOpeningHoursFromPayeeCatalog } from "../../utils/payeeCatalogUtils"
import { validateOrderingPayeeOrder, getPayeeOrderCatalogMetadata  } from "../../utils/payeeOrderUtils"
import { timestampToDateString } from "../../utils/datetimeUtils"

import * as actions from "../../actions"
/**
 * @param {string} cartId
 * @param {string} payeeId
 * @param {string} payeeCatalogId
 * @param {string} payeeHandleId
 */
const PlaceOrderButton = ({
    cartId="",
    payeeId="",
    payeeCatalogId="",
    payeeHandleId=""
}) => {
    const [checkoutModalOpen, setCheckoutModalOpen] = useState(false)
    const cart = useSelector(state => state.carts.cartsById[cartId])
    /** listen to the current order as soon as a current order id is provided*/
    const {currentPayeeOrderId=""} = cart || {}
    const [payeeOrderListener, setPayeeOrderListener] = useState(()=>()=>{})
    const dispatch = useDispatch()
    const listenToPayeeOrder = async () => {
        const newPayeeOrderListener = await dispatch(actions.fetchSubscribeToPayeeOrder(currentPayeeOrderId))
        setPayeeOrderListener(() => newPayeeOrderListener)
    }
    useEffect(() => {
        if (currentPayeeOrderId) listenToPayeeOrder()
        return () => {
            if (typeof payeeOrderListener === "function") payeeOrderListener()
        }
    }, [currentPayeeOrderId])
    /** get details from the catalog */
    const payeeCatalog = useSelector(state => state.payeeCatalogs.payeeCatalogsById[payeeCatalogId])
    const {name="", minimumSpendXcd=0, catalogType=""} = payeeCatalog || {}
    
    //alert the buyer that they must spend at least the minimum spend 
    //if their total is less than the mininimum spend
    //otherwise, create the order
    const openingHours = getOpeningHoursFromPayeeCatalog(payeeCatalog)
    const {isOpen, nextOpeningTime} = useIsOpen(openingHours)
    const handleCreateOrder = () => setCheckoutModalOpen(true)
    const payee = useSelector(state => state.payees.payeesById[payeeId])
    const {transactionLimitXcd=0, name:payeeName=""} = payee || {}
    const cartTotalXcd = getCartTotal(cart)
    let errors = []
    const context = {
        cartTotalXcd,
        cart,
        minimumSpendXcd,
        transactionLimitXcd,
        name,
        payeeName,
        catalogType,
        openingHours,
        isOpen,
        nextOpeningTime,
        payeeCatalog
    }
    const validateOrder = () => {
        
        /** General validations - applying to all or multiple catalog types */
        if (cartTotalXcd < minimumSpendXcd){
            errors.push(`You must spend at least EC$${minimumSpendXcd} to buy from the ${name} catalog.\n\n Your current total is $${Number(cartTotalXcd).toFixed(2)}`)
        }

        else if (transactionLimitXcd && (cartTotalXcd > transactionLimitXcd)){
            errors.push(`Your total cannot be more than $${transactionLimitXcd}`)
        }
                                    
        //if the catalog is one that operates with opening hours 
        //and it is currently closed 
        else if (
            catalogType && 
            [PAYEE_CATALOG_TYPE_ORDERING].includes(catalogType) && 
            (openingHours && Object.keys(openingHours).length > 0) &&
            !isOpen
        ){
            errors.push(`Sorry, the ${name} store by ${payeeName} is closed. Order after it re-opens at ${timestampToDateString(nextOpeningTime, "t 'on' EEEE, dd MMMM")}`)
        }
        /**
         * Handle catalog-specific validation here
         * 
         * TODO: as more specific catalogs are added, add validation code for each one 
        */
        if (errors.length === 0) {
            if (catalogType === PAYEE_CATALOG_TYPE_ORDERING){
                errors = [
                    ...errors,
                    ...validateOrderingPayeeOrder(context)
                ]
            }
        } 
        if (errors.length > 0){
            alert(errors[0])
            return false
        }
        return true
    }
    const handleClickPlaceOrder = () => {
        if (validateOrder()) {
            handleCreateOrder()
        }
    }
    //get the catalog metadata that will be added onto the payee order
    //this will vary by catalog type, which is what is handed by this function, 
    //based on the context passed 
    const orderCatalogMetadata = getPayeeOrderCatalogMetadata(context)
    /* 
        After a successful order, redirect from the Cart / Checkout page 
        to the receipt page if the payee order's receiptId changes to be truthy 
    */
    const currentPayeeOrder = useSelector(state => state.payeeOrders.payeeOrdersById[currentPayeeOrderId])
    const receiptId = currentPayeeOrder && currentPayeeOrder.receiptId ? currentPayeeOrder.receiptId : ""
    if (receiptId) {
        //delete the cart
        //we delay it to avoid interfering with the redirect to Catalog that happens if there is no cart
        setTimeout(() => dispatch(actions.clearCart(cartId)), 200)
        return <Navigate to={`/${payeeHandleId}/r/${receiptId}`}/>
    }
    return (
        <>
            <button 
                className="button success"
                //if the buyer has not met or passed the minimum spend, then remind them of the min spend
                //otherwise, create the order
                onClick={handleClickPlaceOrder} 
            >
                Place Order
            </button>
            {/* opens checkout flow when "place order" is pressed */}
            {
                checkoutModalOpen ? 
                <Modal
                    isOpen={checkoutModalOpen}
                    closeModal={() => setCheckoutModalOpen(false)}
                    className="fit"
                    overlayClassName="center"
                    closeOnOverlayClick={true}
                    showCloseButton={true}
                >
                    <PayeeOrderCheckout 
                        cartId={cartId} 
                        payeeId={payeeId}
                        catalogMetadata={orderCatalogMetadata}
                    />
                </Modal>
                :
                null
            }
        </>
    )
}

export default PlaceOrderButton